import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  SvgIcon,
  SvgIconProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { CloseBig, Refresh02, Shuffle } from "@tiny/icons";
import { useDrawerInvokers } from "@tiny/lib";
import {
  formatAmount,
  formatNumber,
  formatPercent,
} from "@tiny/utils/formatUtils";
import {
  useDeleteWorkflowSplitWinnerMutation,
  useGetWorkflowSplitStatsQuery,
  usePutWorkflowSplitWinnerMutation,
} from "../../app/useGeneratedApi";
import { WorkflowSplitBranchStatsView } from "../../app/GeneratedApi";
import { useConfigView } from "../../Config.context";
import { extractErrorMessage } from "../../utils/errorUtils";
import { getSplitGoalLabel } from "../../pages/WorkflowsBuilderPage/WorkflowsBuilder/Split/split.helpers";
import {
  canClearWinner,
  canDeclareWinner,
  CLEAR_WINNER_CONFIRMATION,
  formatSplitDateTime,
  getDeclareWinnerConfirmation,
  getGoalColumnKey,
  getLeaderLabel,
  getSignificanceLine,
  getSplitStatusBadge,
  getTestWindowLabel,
  getWinnerDecisionLabel,
  SPLIT_STATS_COLUMNS,
  SPLIT_STATS_FOOTNOTE,
  SplitStatsColumnFormat,
} from "./SplitStatsPanel.helpers";

const TrophyIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94A5.01 5.01 0 0 0 11 15.9V19H7v2h10v-2h-4v-3.1a5.01 5.01 0 0 0 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />
  </SvgIcon>
);

type PendingAction =
  | { type: "declare"; branch: WorkflowSplitBranchStatsView }
  | { type: "clear" };

const noop = () => {};

const SplitStatsPanel = ({
  workflowId,
  nodeId,
}: {
  workflowId: string;
  nodeId: string;
}) => {
  const { hideDrawer } = useDrawerInvokers();
  const { config } = useConfigView();
  const [pending, setPending] = React.useState<PendingAction | null>(null);
  const [actionError, setActionError] = React.useState<string>();

  // Errors are shown inline here, so the global snackbar is switched off.
  const {
    data: stats,
    isLoading,
    error: statsError,
    refetch,
  } = useGetWorkflowSplitStatsQuery(workflowId, nodeId, { onError: noop });
  const { mutateAsync: declareWinner, isLoading: isDeclaring } =
    usePutWorkflowSplitWinnerMutation({ onError: noop });
  const { mutateAsync: clearWinner, isLoading: isClearing } =
    useDeleteWorkflowSplitWinnerMutation({ onError: noop });

  const formatValue = (value: number, format: SplitStatsColumnFormat) => {
    if (format === "currency") return formatAmount(value, config?.currency);
    if (format === "percent") return formatPercent(value);
    return formatNumber(value);
  };

  const confirmPending = async () => {
    if (!pending) return;
    setActionError(undefined);
    try {
      if (pending.type === "declare") {
        await declareWinner({
          id: workflowId,
          nodeId,
          branchId: pending.branch.branchId,
        });
      } else {
        await clearWinner({ id: workflowId, nodeId });
      }
      setPending(null);
      refetch();
    } catch (error) {
      setPending(null);
      setActionError(extractErrorMessage(error));
    }
  };

  const renderHeader = () => {
    const badge = stats ? getSplitStatusBadge(stats) : undefined;
    return (
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Shuffle sx={{ color: "fuchsia.150" }} />
          <Typography variant="h2">{stats?.name || "Traffic Split"}</Typography>
          {stats?.goal && (
            <Typography variant="label1" color="grey.80">
              {stats.status === "LIFETIME" ? "Tracking" : "Goal"}:{" "}
              {getSplitGoalLabel(stats.goal)}
            </Typography>
          )}
          {badge && (
            <Chip
              size="small"
              color={badge.color}
              label={badge.label}
              icon={
                stats?.winner ? <TrophyIcon sx={{ fontSize: 16 }} /> : undefined
              }
              data-testid="split-status-badge"
            />
          )}
        </Box>
        <IconButton sx={{ px: 0 }} onClick={hideDrawer}>
          <CloseBig />
        </IconButton>
      </Box>
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box mt={6}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="rectangular" height={160} sx={{ mt: 4 }} />
        </Box>
      );
    }

    if (statsError || !stats) {
      return (
        <Alert
          severity="error"
          sx={{ mt: 6 }}
          action={
            <Button
              size="small"
              startIcon={<Refresh02 sx={{ fontSize: 12 }} />}
              onClick={() => refetch()}
            >
              Reload
            </Button>
          }
        >
          {extractErrorMessage(statsError) || "Failed to load split results"}
        </Alert>
      );
    }

    const goalKey = getGoalColumnKey(stats.goal);
    const testWindow = getTestWindowLabel(stats);
    const significance = getSignificanceLine(stats);

    return (
      <Box mt={4} display="flex" flexDirection="column" gap={3}>
        {stats.winner && (
          <Typography variant="label1" color="grey.90">
            {getWinnerDecisionLabel(stats.winner)} ·{" "}
            {formatSplitDateTime(stats.winner.decidedAt)}
          </Typography>
        )}
        {testWindow && (
          <Typography variant="label1" color="grey.90">
            {testWindow}
          </Typography>
        )}
        {significance && (
          <Typography
            variant="label1"
            color="grey.100"
            data-testid="split-significance"
          >
            {significance}
          </Typography>
        )}

        {actionError && (
          <Alert severity="error" onClose={() => setActionError(undefined)}>
            {actionError}
          </Alert>
        )}

        <TableContainer
          sx={(theme) => ({
            border: `1px solid ${theme.palette.grey[40]}`,
            borderRadius: 2,
          })}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Path</TableCell>
                {SPLIT_STATS_COLUMNS.map((column) => (
                  <TableCell
                    key={column.key}
                    align="right"
                    sx={(theme) => ({
                      whiteSpace: "nowrap",
                      ...(column.key === goalKey && {
                        backgroundColor: theme.palette.primary[10],
                        fontWeight: 700,
                      }),
                    })}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.branches.map((branch) => {
                const isWinner = stats.winner?.branchId === branch.branchId;
                const leaderLabel =
                  stats.leaderBranchId === branch.branchId
                    ? getLeaderLabel(stats)
                    : null;
                return (
                  <TableRow
                    key={branch.branchId}
                    sx={(theme) => ({
                      ...(isWinner && {
                        backgroundColor: theme.palette.green[50],
                      }),
                    })}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {isWinner && (
                          <TrophyIcon
                            sx={(theme) => ({
                              fontSize: 16,
                              color: theme.palette.yellow[150],
                            })}
                            titleAccess="Winner"
                          />
                        )}
                        <Typography variant="label1" fontWeight={600}>
                          {branch.name}
                        </Typography>
                        {leaderLabel && !isWinner && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={leaderLabel}
                          />
                        )}
                      </Box>
                    </TableCell>
                    {SPLIT_STATS_COLUMNS.map((column) => (
                      <TableCell
                        key={column.key}
                        align="right"
                        sx={(theme) => ({
                          whiteSpace: "nowrap",
                          ...(column.key === goalKey && {
                            backgroundColor: theme.palette.primary[10],
                            fontWeight: 700,
                          }),
                        })}
                      >
                        {formatValue(
                          branch[column.key] as number,
                          column.format
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {canDeclareWinner(stats, branch.branchId) && (
                        <Button
                          size="small"
                          onClick={() =>
                            setPending({ type: "declare", branch })
                          }
                        >
                          Declare winner
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {canClearWinner(stats) && (
          <Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setPending({ type: "clear" })}
            >
              Clear winner
            </Button>
          </Box>
        )}

        <Typography variant="label2R" color="grey.80">
          {SPLIT_STATS_FOOTNOTE}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        py: 4,
        px: 6,
        width: { xs: "90vw", lg: "1100px" },
        maxWidth: "100vw",
      }}
    >
      {renderHeader()}
      {renderBody()}

      <Dialog
        open={!!pending}
        onClose={() => setPending(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h3">
            {pending?.type === "declare"
              ? `Declare ${pending.branch.name} the winner?`
              : "Clear the winner?"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="label1" color="grey.90">
            {pending?.type === "declare"
              ? getDeclareWinnerConfirmation(pending.branch.name)
              : CLEAR_WINNER_CONFIRMATION}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmPending}
            disabled={isDeclaring || isClearing}
          >
            {pending?.type === "declare" ? "Declare winner" : "Clear winner"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SplitStatsPanel;
